<script setup lang="ts">
export type AccordionItemColor = 'primary' | 'secondary'

export interface AccordionItemProps {
    btnText?: string
    btnIndex: number
    isOpen?: boolean
    color?: AccordionItemColor
}

const props = withDefaults(defineProps<AccordionItemProps>(), {
    btnText: '',
    btnIndex: 0,
    color: 'primary',
})

const { $t } = useNuxtApp()
const { data, sendEvent } = useTracker()

// Refs for accessibility (WCAG 4.1.2)
const detailsRef = ref<HTMLDetailsElement>()
const panelRef = ref<HTMLElement>()
const isExpanded = ref(props.isOpen ?? false)
const stateAnnouncement = ref<string>('')

const handleClick = (): void => {
    sendEvent({
        data: {
            ...data,
            click: props.btnText,
        },
    })
}

// Handle toggle event - announce state change (WCAG 4.1.2)
const handleToggle = (): void => {
    if (detailsRef.value) {
        const newState = detailsRef.value.open
        isExpanded.value = newState

        // Announce state change to screen readers
        stateAnnouncement.value = ''
        setTimeout(() => {
            if (newState) {
                stateAnnouncement.value = `${props.btnText}, ${$t('rozbalené')}`
            } else {
                stateAnnouncement.value = `${props.btnText}, ${$t('zbalené')}`
            }
        }, 100)
    }
}

onMounted(() => {
    detailsRef.value?.addEventListener('toggle', handleToggle)
})

onUnmounted(() => {
    detailsRef.value?.removeEventListener('toggle', handleToggle)
})
</script>

<template>
  <li class="accordion-item-host">
    <details
      ref="detailsRef"
      :open="props.isOpen"
      :class="`accordion-item-${props.color}`"
      class="accordion-item bg-white box-shadow-1 rounded-4 overflow-hidden mb-2"
    >
      <summary
        :id="`accordion-summary-${props.btnIndex}`"
        :aria-expanded="isExpanded"
        :aria-controls="`accordion-panel-${props.btnIndex}`"
        class="accordion-item-summary d-flex align-items-center justify-content-between py-3 px-4 py-md-5 px-md-6 fs-smaller fs-md-bigger fw-bold"
        @click="handleClick"
      >
        {{ props.btnText }}
        <svg
          aria-hidden="true"
          class="accordion-item-icon-open accordion-item-icon transition ms-2 flex-shrink-0"
          width="16"
          height="8"
        >
          <use xlink:href="#angle-down" />
        </svg>
        <svg
          aria-hidden="true"
          class="accordion-item-icon-close accordion-item-icon transition ms-2 flex-shrink-0"
          width="14"
          height="14"
        >
          <use xlink:href="#times" />
        </svg>
      </summary>
      <div
        :id="`accordion-panel-${props.btnIndex}`"
        ref="panelRef"
        class="accordion-panel"
        role="region"
        :aria-labelledby="`accordion-summary-${props.btnIndex}`"
        :tabindex="isExpanded ? 0 : -1"
      >
        <slot />
      </div>
    </details>
    <!-- Screen reader announcement for state changes (WCAG 4.1.2) -->
    <Teleport to="body">
      <div
        aria-live="assertive"
        aria-atomic="true"
        class="visually-hidden"
      >
        {{ stateAnnouncement }}
      </div>
    </Teleport>
  </li>
</template>
<style lang="scss" scoped>
@import './AccordionItem.scss';
</style>
